/**
 * Data layer for `row-actions-menu-edit-pause-duplicate-delete`. Thin typed fetch wrappers over /api/v1/row-actions-menu-edit-pause-duplicate-delete.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface RowActionsMenuEditPauseDuplicateDeleteRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListRowActionsMenuEditPauseDuplicateDeleteResponse {
	count: number;
	items: RowActionsMenuEditPauseDuplicateDeleteRow[];
}

export interface CreateRowActionsMenuEditPauseDuplicateDeleteBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/row-actions-menu-edit-pause-duplicate-delete";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListRowActionsMenuEditPauseDuplicateDeleteResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListRowActionsMenuEditPauseDuplicateDeleteResponse>(res);
}

export async function create(body: CreateRowActionsMenuEditPauseDuplicateDeleteBody): Promise<RowActionsMenuEditPauseDuplicateDeleteRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<RowActionsMenuEditPauseDuplicateDeleteRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const rowActionsMenuEditPauseDuplicateDeleteQueryKeys = {
	all: ["row-actions-menu-edit-pause-duplicate-delete"] as const,
	list: () => [...rowActionsMenuEditPauseDuplicateDeleteQueryKeys.all] as const,
	detail: (id: string) => [...rowActionsMenuEditPauseDuplicateDeleteQueryKeys.all, id] as const,
};
